import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { HttpClient } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { Radicacion, RadicadoCita, Const, ModificacionDocumento } from "@app/shared";
import { HttpErrorHandlingService, EndpointService } from "@app/core";
import { environment } from "@env/environment";

/**
 * Modelo de la generación de radicados
 *
 * @export
 * @interface GenerateRadication
 */
export interface GenerateRadication {
  clasificacion_documentos: number;
  documento_cita: string;
  fecha: Date;
  folios: number;
  id: number;
  id_documento_cita: number;
  radicado: string;
  id_dependencia: number;
  expediente?: number;
  radicado_expediente?: string;
}

/**
 * Modelo de las dependencias
 *
 * @export
 * @interface Dependencie
 */
export interface Dependencie {
  id: number;
  nombre: string;
  descripcion: string;
  codigo: number;
}

export interface Sticker {
  id?: number;
  nombre?: string;
  tipo_documental?: TipoDocumental;
  imprimir?: boolean;
}

export interface TipoDocumental {
  id: number;
  nombre: string;
  codigo: string;
  expediente: boolean;
  subserie: number;
}

/**
 * Modelo de un año de radicados
 *
 * @export
 * @interface RadicationYear
 */
export interface RadicationYear {
  anio: number;
}

const radicacion_cinco = environment.radicacion_cinco;

@Injectable()
export class RadicationDocsDataService {
  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private hehs: HttpErrorHandlingService,
    private api: EndpointService
  ) {}

  /**
   * Peticion que radica el documento seleccionado
   *
   * @param {Radicacion} radicate
   * @returns {Observable<any>}
   * @memberof RadicationDataService
   */
  updateRadicateDocument(radicate: Radicacion): Observable<any> {
    let configData: any;

    configData = {
      id: radicate.idDocumento,
      radicado: radicate.radicado,
      fecha: radicate.fecha,
      folios: radicate.folios,
    };

    return new Observable((obs) => {
      this.http
        .put(this.api.get("radication", [radicate.idDocumento]), configData, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            obs.next(res.body);
            this.toastr.success("Se ha radicado el documento correctamente.", "Operación exitosa");
          },
          (err) =>
            this.hehs.error(err, () => {
              obs.error(err);
            })
        );
    });
  }

  /**
   * Servicio encargado de subir los archivos
   *
   * @param {FormData} formData
   * @returns {Observable<any>}
   * @memberof RadicationDataService
   */
  uploadFile(formData: FormData): Observable<any> {
    return new Observable((obs) => {
      this.http
        .post(this.api.get("docsInstance"), formData, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            if (res.status === 201) {
              obs.next(res.body);
              this.toastr.success("Archivos subidos correctamente.", "Operación exitosa");
            } else {
              obs.next({});
            }
          },
          (err) =>
            this.hehs.error(err, () => {
              obs.error(err);
            })
        );
    });
  }

  /**
   * servicio para descargar los archivos
   *
   * @param {number} idDocumento
   * @returns {Observable<any>}
   * @memberof RadicationDataService
   */
  dowloadFile(idDocumento: number): Observable<any> {
    return new Observable((obs) => {
      this.http
        .skipErrorHandler()
        .get(this.api.get("docsInstance", [idDocumento]), {
          observe: "response",
        })
        .subscribe(
          (res) => {
            obs.next(res.body);
          },
          (err) => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error(err.error["message"], "Fallo de descarga");
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   * Consulta los radicados de cita
   *
   * @param {string} dateInit
   * @param {string} dateFinish
   * @param {number} idTrd
   * @param {number} [consecutive=0]
   * @param {number} [idDocument=-1]
   * @returns {Observable<RadicadoCita[]>}
   * @memberof RadicationDocsDataService
   */
  getRadicationsCita(
    year: number,
    idTrd: number,
    consecutive: number = 0,
    idDocument: number = -1,
    id_tipo_documental?: number
  ): Observable<RadicadoCita[]> {
    const formData = new FormData();

    if(radicacion_cinco){
      formData.append('year', String(year));
      formData.append('tipo_documental', String(id_tipo_documental))
    }else{
      formData.append("anio", String(year));
      formData.append("idtrd", String(idTrd));

      if (idDocument !== -1) {
        formData.append("iddocumento", String(idDocument));
      }

      if (consecutive) {
        formData.append("consecutivo", String(consecutive));
      }
    }

    if (radicacion_cinco) {
      return new Observable((obs) => {
        this.http
          .skipErrorHandler()
          .post<RadicadoCita[]>(this.api.get("anios_radicados"), formData, {
            observe: "response",
          })
          .subscribe(
            (res) => {
              obs.next(res.body);
            },
            (err) =>
              this.hehs.error(err, () => {
                obs.error(err);
              })
          );
      });
    } else {
      return new Observable((obs) => {
        this.http
          .skipErrorHandler()
          .post<RadicadoCita[]>(Const.ROUTES_SYSTEM_INTEGRATIONS.cita.index, formData, {
            observe: "response",
          })
          .subscribe(
            (res) => {
              obs.next(res.body);
            },
            (err) =>
              this.hehs.error(err, () => {
                obs.error(err);
              })
          );
      });
    }
  }


  /**
   * Consulta los radicados de por serie y subserie
   *
   * @param {string} dateInit
   * @param {string} dateFinish
   * @param {number} idTrd
   * @param {number} [consecutive=0]
   * @param {number} [idDocument=-1]
   * @returns {Observable<RadicadoCita[]>}
   * @memberof RadicationDocsDataService
   */
  getRadicationsSerie(
    year: number,
    id_tipo_documental?: number,
    propiedades_asociacion?: any,
  ): Observable<RadicadoCita[]> {
    const formData = new FormData();
    formData.append('year', String(year));
    formData.append('tipo_documental', String(id_tipo_documental))
    if(propiedades_asociacion){
      let listSerie: any[] = [];
      let listSubserie: any[] = [];
      propiedades_asociacion.forEach((element: { subserie: any; serie: any; }) => {
        if(element.subserie){
          listSubserie.push(element.subserie);
        }else{
          listSerie.push(element.serie);
        }
      });
      if(listSerie.length){
        formData.append('serie', JSON.stringify(listSerie));
      }
      if(listSubserie.length){
        formData.append('subserie', JSON.stringify(listSubserie));
      }
    }
    return new Observable((obs) => {
      this.http
        .skipErrorHandler()
        .post<RadicadoCita[]>(this.api.get("anios_radicados"), formData, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            obs.next(res.body);
          },
          (err) =>
            this.hehs.error(err, () => {
              obs.error(err);
            })
        );
    });
  }


  /**
   * Consulta los años de los radicados disponibles en cita
   *
   * @param {string} idTrd
   * @returns {Observable<RadicadoCita[]>}
   * @memberof RadicationDocsDataService
   */
  getRadicationYears(idTrd: string, id_tipo_documental?: number): Observable<RadicationYear[]> {
    const formData = new FormData();

    formData.append("idtrd", idTrd);

    if (radicacion_cinco) {
      return new Observable((obs) => {
        this.http.get<RadicationYear[]>(this.api.get("anios_radicados", [id_tipo_documental])).subscribe(
          (res) => {
            obs.next(res);
          },
          (err) =>
            this.hehs.error(err, () => {
              obs.error(err);
            })
        );
      });
    } else {
      return new Observable((obs) => {
        this.http
          .skipErrorHandler()
          .post<RadicationYear[]>(Const.ROUTES_SYSTEM_INTEGRATIONS.cita.index, formData, {
            observe: "response",
          })
          .subscribe(
            (res) => {
              obs.next(res.body);
            },
            (err) =>
              this.hehs.error(err, () => {
                obs.error(err);
              })
          );
      });
    }
  }

  /**
   * Consulta los años de los radicados disponibles en por serie y subserie
   *
   * @param {string} idTrd
   * @returns {Observable<RadicadoCita[]>}
   * @memberof RadicationDocsDataService
   */
  getRadicationYearsBySerie(asociacion: any): Observable<RadicationYear[]> {
    const formData = new FormData();
    if(asociacion){
      let listSerie: any[] = [];
      let listSubserie: any[] = [];
      asociacion.forEach((element: { subserie: any; serie: any; }) => {
        if(element.subserie){
          listSubserie.push(element.subserie);
        }else{
          listSerie.push(element.serie);
        }
      });
      if(listSerie.length){
        formData.append('serie', JSON.stringify(listSerie));
      }
      if(listSubserie.length){
        formData.append('subserie', JSON.stringify(listSubserie));
      }
    }
    return new Observable((obs) => {
      this.http.post<RadicationYear[]>(this.api.get('associateRadicationUtilitiesService'), formData).subscribe(
        (res) => {
          obs.next(res);
        },
        (err) =>
          this.hehs.error(err, () => {
            obs.error(err);
          })
      );
    });
  }

  /**
   * Consulta las dependencias disponibles
   *
   * @param {string} idTrd
   * @returns {Observable<RadicadoCita[]>}
   * @memberof RadicationDocsDataService
   */
  getDependencies(): Observable<any> {
    return new Observable((obs) => {
      this.http
        .get<any>(this.api.get("searchDependencie"), {
          observe: "response",
        })
        .subscribe(
          (res) => {
            obs.next(res.body);
          },
          (err) => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error(err.error["message"], "Fallo en la consulta de datos de dependencias");
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   * Ejecuta la radicacion del documento
   *
   * @param {*} formData
   * @param {number} [iddocumento]
   * @returns {Promise<GenerateRadication>}
   * @memberof RadicationDocsDataService
   */
  setRadication(formData: any, iddocumento?: number): Promise<GenerateRadication> {
    return new Promise((resolve, reject) => {
      const action = iddocumento ? "put" : "post";

      this.http
        .skipErrorHandler()
        [action]<GenerateRadication>(this.api.get("radicationDocument", [iddocumento]), formData, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            this.toastr.success(`Documento radicado correctamente`, "Operación exitosa");
            resolve(res.body);
          },
          (err) => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error(
                "Lo sentimos el documento no se ha podido asociar al expediente",
                "Falla de radicación"
              );
            }
            reject(false);
          }
        );
    });
  }

  /**
   * Servicio que genera el documento
   *
   * @param {*} data
   * @returns {Observable<any>}
   * @memberof RadicationDocsDataService
   */
  generateDocument(data: any): Observable<any> {
    return new Observable((obs) => {
      this.http
        .skipErrorHandler()
        .post(this.api.get("documentGeneration"), data, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            obs.next(res.body);
          },
          (err) => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error(err.error["message"], "Error en radicación");
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   * Genera el radicado del documento
   *
   * @param {number} folios
   * @param {number} idTarea
   * @param {number} classificationDocument
   * @param {number} id_dependencia
   * @param {Array<any>} [indices]
   * @returns {Promise<GenerateRadication>}
   * @memberof RadicationDocsDataService
   */
  generateRadication(
    folios: number,
    idTarea: number,
    classificationDocument: number,
    indices?: Array<any>,
    radicate?: string,
    dateRadicate?: Date | string,
    id_dependencia?: number,
    stickerId?: number,
    generarQR?: boolean,
    tipoQR?: any,
    categoriaQR?: number,
    expId?: number,
    label_radicado_expediente?: string
  ): Promise<GenerateRadication> {
    const dataForm = {
      folios: folios,
      tarea: idTarea,
      clasificacion_documentos: classificationDocument,
      id_dependencia: id_dependencia,
    };
    if (indices && indices.length) {
      dataForm["indices"] = indices;
    }
    if (radicate && dateRadicate) {
      dataForm["radicado"] = radicate;
      let date: Date;
      if (typeof dateRadicate === "string") {
        date = new Date(dateRadicate);
      } else {
        date = dateRadicate;
      }
      const moment = require("moment");
      dataForm["fecha"] = moment(date).format("YYYY-MM-DD HH:mm:ss");
    }
    if(stickerId){
      dataForm["stiker_id"] = stickerId;
      dataForm["insertar_sticker"] = true;
    }
    if(generarQR){
      dataForm["generarQR"] = generarQR;
      dataForm["tipoQR"] = tipoQR;
      dataForm["categoriaQR"] = categoriaQR;
    }
    if(expId){
      dataForm["expediente"] = expId;
      dataForm["asociar_expediente"] = true;
      dataForm["label_radicado_expediente"] = label_radicado_expediente;
    }
    return new Promise((resolve, reject) => {
      this.http
        .skipErrorHandler()
        .post<GenerateRadication>(this.api.get("generateRadicationCita"), dataForm, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            this.toastr.success(`Documento radicado correctamente`, "Operación exitosa");
            resolve(res.body);
          },
          (err) =>{
            if (err.status !== 401 && err.status !== 403) {
              if (err.error["message"] && err.status !== 500) {
                this.toastr.warning(err.error["message"], "Error en radicación");
              } else if (err.error["Error"] && err.status !== 500) {
                this.toastr.warning(err.error["Error"], "Error en radicación");
              } else if (err.status !== 500) {
                this.toastr.error("Lo sentimos ha ocurrido un error inesperado", "Error en radicación");
              }
              else if (err.status == 500) {
                this.toastr.error("Lo sentimos ha ocurrido un error inesperado", "Error en radicación");
              }
            }
            reject(err);
          } 
        );
    });
  }

  getInfoRecordCover(idDocument: number): Observable<any> {
    return new Observable((obs) => {
      this.http
        .skipErrorHandler()
        .get<any>(this.api.get("generateRecordCover", [idDocument]), {
          observe: "response",
        })
        .subscribe(
          (res) => {
            obs.next(res.body);
          },
          (err) => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error(err.error["message"], "Fallo de descarga");
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   *Obtiene los stickers por el tipo documental
   *
   * @param {number} id
   * @return {*}  {Observable<Sticker[]>}
   * @memberof RadicationDocsDataService
   */
  getStickers(id: number): Observable<Sticker[]> {
    return new Observable((obs) => {
      this.http.get<Sticker[]>(this.api.get("sticker_document", [id])).subscribe(
        (sticker) => {
          obs.next(sticker);
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }

  /**
   *Descragr el sticker por el tipo documental
   *
   * @param {number} id_tarea
   * @param {number} sticker_id
   * @param {number} [clasificacion]
   * @return {*}  {Observable<any>}
   * @memberof RadicationDocsDataService
   */
  downloadSticker(id_tarea: number, sticker_id: number, clasificacion?: number): Observable<any> {
    let data = {
      tarea: id_tarea,
      clasificacion: clasificacion,
      sticker_id: sticker_id,
    };
    return new Observable((obs) => {
      this.http
        .post<any>(this.api.get("generateStickerDoc"), data, {
          observe: "response",
        })
        .subscribe(
          (res) => {
            if (res.status === 200 || res.status === 201) {
              obs.next(res);
              this.toastr.success("Rótulo descargado exitosamente.", "Operación exitosa");
            } else {
              obs.next({});
            }
          },
          (err) =>
            this.hehs.error(err, () => {
              obs.error(err);
            })
        );
    });
  }

   /**
   * Consulta los expedientes para el documento
   *
   * @param {number} id_tarea
   * @returns {Observable<any>}
   * @memberof RadicationDocsDataService
   */
    getExp(
      id_tarea: number,
      id_tipo_documental?: number,
    ): Observable<any> {
      const formData = new FormData();
      formData.append('id_tarea', String(id_tarea));
      formData.append('tipo_documental', String(id_tipo_documental));
      formData.append('nombre', "_buscador_expedientes_radicacion");
      return new Observable((obs) => {
        this.http
          .skipErrorHandler()
          .post<any>(this.api.get("advancedSearchs"), formData, {
            observe: "response",
          })
          .subscribe(
            (res) => {
              obs.next(res.body);
            },
            (err) =>
              this.hehs.error(err, () => {
                obs.error(err);
              })
          );
      });
    }

    /**
   * servicio para descargar los archivos
   *
   * @param {number} idDocumento
   * @returns {Observable<any>}
   * @memberof RadicationDocsDataService
   */
  dowloadModifyFile(idDocumento: number): Observable<any> {
    return new Observable(obs => {
      this.http
        .skipErrorHandler()
        .get(this.api.get('modifyDocument', [idDocumento]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(res.body);
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error(err.error['message'], 'Fallo de descarga');
            }
            obs.error(err);
          }
        );
    });
  }


   /**
   *Obtiene el historial de modificaciones de un documento
   *
   * @param {number} id
   * @return {*}  {Observable<ModificacionDocumento[]>}
   * @memberof RadicationDocsDataService
   */
   getHistoryModifyDocument(id: number): Observable<ModificacionDocumento[]> {
    return new Observable((obs) => {
      this.http.get<ModificacionDocumento[]>(this.api.get("historyModifyDocument", [id])).subscribe(
        (history: any) => {
          obs.next(history);
        },
        (err) => {
          obs.error(err);
        }
      );
    });
  }
    
}



// WEBPACK FOOTER //
// ./src/app/forms/shared/radication-docs/shared/radication-docs-data.service.ts